import * as _ from 'lodash-es';
import React from 'react';
import type { SetRequired } from 'type-fest';
import * as styles from './TextWithIcon.module.scss';
import Link from '../utils/router/Link';

interface WrapGroupProps {
  text: React.ReactNode;
  icon: React.ReactNode;
  noWrap?: boolean;
}
const WrapGroup = React.forwardRef<HTMLSpanElement, WrapGroupProps>(
  ({ text, icon, noWrap }, ref) => {
    let leadingComponent = text;
    let trailingComponent = icon;

    if (typeof text === 'string') {
      const chars = _.split(text, '');
      const rest = chars.slice(0, chars.length - 1).join('');
      const last = chars[chars.length - 1];
      leadingComponent = rest;
      trailingComponent = (
        <span className={styles.wrappingGroup}>
          {last}
          {icon}
        </span>
      );
    }

    return (
      <span ref={ref} className={noWrap ? styles.wrappingGroup : styles.wrap}>
        {leadingComponent}
        {trailingComponent}
      </span>
    );
  },
);

interface TruncateContentProps {
  icon?: React.ReactNode;
  text: string;
}

const SingeLineTruncateContent = ({ icon, text }: TruncateContentProps) => {
  return (
    <>
      <span className={styles.singleLine}>{text}</span>
      {icon}
    </>
  );
};

type TextWithIconProps = {
  icon?: React.ReactNode;
  noWrap?: boolean;
  to?: string;
} & (
  | {
      truncate: boolean;
      text: string;
    }
  | {
      truncate?: never;
      text: React.ReactNode;
    }
);

const TruncatedTextWithIcon = React.forwardRef<
  HTMLDivElement,
  SetRequired<Omit<TextWithIconProps, 'noWrap'>, 'truncate'> & {
    text: string;
  }
>(({ text, icon, to }, ref) => {
  const content = <SingeLineTruncateContent icon={icon} text={text} />;

  return (
    <div ref={ref} className={styles.truncate}>
      {to ? <Link to={to}>{content}</Link> : content}
    </div>
  );
});

const TextWithIcon = React.forwardRef<HTMLDivElement, TextWithIconProps>(
  (props, ref) => {
    if (props.truncate) {
      return (
        <TruncatedTextWithIcon ref={ref} {...props} truncate={props.truncate} />
      );
    }

    return (
      <WrapGroup
        ref={ref}
        text={props.text}
        icon={props.icon}
        noWrap={props.noWrap}
      />
    );
  },
);

export default TextWithIcon;
